import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import PickCard from 'components/PickCard/PickCard';
import PicksFilters from 'components/PicksFilters/PicksFilters';
import Spinner from 'components/Spinner/Spinner';
import Button from 'components/Button/Button';
import { listPicks, getFavorites } from 'services/picksApi';
import { listGames } from 'services/gamesApi';
import { PickDetailGameInfo } from 'pages/PickDetail/PickDetail';
import { GROUP_NAMES, DEFAULT_SPORT } from 'utils';
import type { ApiPick, PicksFilterValues, ApiGame, ApiGameListResponse } from 'services/types';
import { t } from '../../helpers/componentHelper';

import './Picks.scss';

type PicksComponentProps = {
  userGroups: string[];
};

// Top-level Picks page — backed by the real /api/v1/picks endpoint.
function Picks({ userGroups }: PicksComponentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { sport: sportFromUrl } = useParams<{ sport?: string }>();
  const state = location?.state as { currentFilters?: PicksFilterValues } | undefined;
  const [filters, setFilters] = useState<PicksFilterValues>({
    ...(state?.currentFilters ?? {
      sport: sportFromUrl ?? DEFAULT_SPORT,
      outcome: 'pending',
    }),
  });
  const [picks, setPicks] = useState<ApiPick[]>([]);
  const [gameById, setGameById] = useState<Record<string, PickDetailGameInfo>>({});
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refresh current filters
  const refresh = () => {
    setPageNum(1);
    setTotal(0);
    setError(null);
    setFilters((f) => ({ ...f }));
  };

  // Reset accumulated picks whenever filters change. URL changes trigger
  // filter state via the useEffect below.
  const resetAndLoad = useCallback((nextFilters: PicksFilterValues) => {
    setPicks([]);
    setPageNum(1);
    setTotal(0);
    setError(null);
    setFilters(nextFilters);
  }, []);

  // Keep filter.sport synced with URL changes (`/picks/:sport`).
  // Intentionally narrow deps to sportFromUrl — including the rest of
  // `filters` would loop because the body updates `filters`.
  useEffect(() => {
    if (sportFromUrl && sportFromUrl !== filters.sport) {
      resetAndLoad({ ...filters, sport: sportFromUrl });
    }
  }, [sportFromUrl]); // eslint-disable-line

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listGames({ sport: filters.sport, pageNum: 0 })
      .then((res: ApiGameListResponse) => {
        if (cancelled) return;
        const gameById: Record<string, PickDetailGameInfo> = {};
        res.results.forEach((game: ApiGame) => {
          gameById[game.eventId] = {
            eventId: game.eventId,
            eventName: game.eventName.replace('Athletics Athletics', 'Athletics'),
            eventStart: game.scheduledAt,
          };
        });
        setGameById({ ...gameById });
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load games';
        setError(String(message));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.sport]);

  // Fetch on (filters | pageNum) change. Append for page > 1.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetchPicks = filters.isFavorite === 'favorites' ? getFavorites : listPicks;
    fetchPicks({ ...filters, batchSize: 150, pageNum })
      .then((res) => {
        if (cancelled) return;
        setTotal(res.total);
        const gameIds = res.results.reduce((acc, pick) => {
          acc.add(pick.eventId);
          return acc;
        }, new Set<string>());
        // Mutate the picks in-place to add the game name for display in the UI.
        res.results.forEach((pick: ApiPick) => {
          if (gameIds.has(pick.eventId)) {
            const gameName = gameById[pick.eventId]?.eventName;
            pick.eventName = gameName ? gameName : pick.eventId; // fallback to ID if name is missing
          }
        });
        setPicks((prev) => {
          let results = pageNum === 1 ? res.results : [...prev, ...res.results];
          if (filters.eventDate === 'today' || filters.eventDate === 'tomorrow') {
            const gameIds = Object.values(gameById).reduce((acc, game) => {
              if (game.eventStart) {
                const scheduledDate = new Date(game.eventStart).toLocaleDateString('en-CA'); // YYYY-MM-DD
                const todayDate = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
                const tomorrowDate = new Date(Date.now() + 86400000).toLocaleDateString('en-CA'); // YYYY-MM-DD
                if (filters.eventDate === 'today' && scheduledDate === todayDate) {
                  acc.add(game.eventId);
                } else if (filters.eventDate === 'tomorrow' && scheduledDate === tomorrowDate) {
                  acc.add(game.eventId);
                }
              }
              return acc;
            }, new Set<string>());
            results = results.reduce((acc, pick) => {
              if (!acc.some((p) => p.pickId === pick.pickId) && gameIds.has(pick.eventId)) {
                acc.push(pick);
              }
              return acc;
            }, [] as ApiPick[]);
          }
          if (filters.parlayLegs) {
            results = results.filter((pick) => pick.parlayLegs === filters.parlayLegs);
          }
          setTotal(results.length); // update total to reflect filtered results
          return results.sort((a, b) => (a.parlayAmericanOdds >= b.parlayAmericanOdds ? 1 : -1));
        });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load picks';
        setError(String(message));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gameById, filters, pageNum]);

  const handleRefresh = () => {
    refresh();
  };

  const handleFilterChange = (next: PicksFilterValues) => {
    resetAndLoad(next);
  };

  const handleLoadMore = () => {
    setPageNum((p) => p + 1);
  };

  const hasMore = picks.length < total;

  return (
    <div className={'picks'}>
      <div className={'picks__header'}>
        <div>{`${t('pages.picks.headerText')}`}</div>
        <div className={'picks__header--subtext'}>{`${t('pages.picks.headerSubtext')}`}</div>
      </div>
      {userGroups.includes(GROUP_NAMES.free) && (
        <div className={'picks__free-info-container'} onClick={() => navigate('/pricing')}>
          <div className={'picks__free-info'}>
            <span>{`${t('pages.picks.freeInfo')}`}</span>
          </div>
        </div>
      )}
      <PicksFilters
        value={filters}
        userGroups={userGroups}
        onChange={handleFilterChange}
        onRefresh={handleRefresh}
        disabled={loading}
      />

      <div className={'picks__meta'}>
        {loading && picks.length === 0 ? (
          <div className={'pick-detail__loading'}>
            <Spinner />
          </div>
        ) : (
          <span>
            {filters.computedDate === 'today'
              ? "Today's"
              : filters.computedDate === 'yesterday'
              ? "Yesterday's"
              : 'All'}{' '}
            picks ({total})
          </span>
        )}
      </div>

      {error && (
        <div className={'picks__error'} role={'alert'}>
          {error}
        </div>
      )}

      <div className={'picks__list'}>
        {picks.map((p) => (
          <PickCard
            key={p.pickId}
            pick={p}
            userGroups={userGroups}
            currentFilters={filters}
            resetAndLoad={refresh}
          />
        ))}

        {!loading && !error && picks.length === 0 && (
          <div className={'picks__empty'}>{`${t(`pages.picks.noResults`)}`}</div>
        )}
      </div>

      {hasMore && (
        <div className={'picks__loadmore'}>
          <Button
            onClick={handleLoadMore}
            active={!loading}
            text={loading ? `${t('pages.picks.loading')}` : `${t('pages.picks.loadMore')}`}
          />
        </div>
      )}
    </div>
  );
}

export default Picks;
